/**
 * Realtime subscription helpers for medication + notification tables
 * (spec §10.6). Realtime is used only for focused tables:
 * medication_occurrences, medication_intake_events, notifications.
 *
 * Handles, per spec:
 * - reconnect: on CHANNEL_ERROR / TIMED_OUT the subscription is re-created
 *   with bounded exponential backoff;
 * - duplicate events: (table, id, updated_at) fingerprints seen within a TTL
 *   window are delivered only once;
 * - stale subscriptions / cleanup: the returned function removes the channel,
 *   cancels pending reconnect timers and invalidates pending callbacks —
 *   always call it on unmount, sign-out or client switch.
 *
 * Subscriptions are filtered server-side (`filter=`) so other users' rows
 * are never pushed to this client; RLS remains the enforcement layer.
 */
import type { RealtimeChannel } from '@supabase/supabase-js'
import { requireSupabase } from '@/lib/supabase/client'
import {
  appNotificationFromRow,
  medicationIntakeEventFromRow,
  medicationOccurrenceFromRow,
  type AppNotificationRow,
  type MedicationIntakeEventRow,
  type MedicationOccurrenceRow,
} from '@/lib/supabase/mappers'
import type { AppNotification, MedicationIntakeEvent, MedicationOccurrence } from '@/types'

export type MedicationRealtimeEvent =
  | { table: 'medication_occurrences'; kind: 'INSERT' | 'UPDATE' | 'DELETE'; row: MedicationOccurrence | null }
  | { table: 'medication_intake_events'; kind: 'INSERT' | 'UPDATE' | 'DELETE'; row: MedicationIntakeEvent | null }
  | { table: 'notifications'; kind: 'INSERT' | 'UPDATE' | 'DELETE'; row: AppNotification | null }

export type SubscribeMedicationRealtimeOptions = {
  /** Occurrences + intake events are filtered on this client id. */
  clientId: string
  /** Notifications are filtered on this recipient (usually the session user). */
  recipientId: string
  onEvent: (event: MedicationRealtimeEvent) => void
  onStatusChange?: (status: 'subscribed' | 'reconnecting' | 'closed') => void
}

const DEDUP_TTL_MS = 30_000
const MAX_RECONNECT_ATTEMPTS = 5
const BASE_BACKOFF_MS = 1_000

type SeenMap = Map<string, number>

function fingerprint(table: string, row: Record<string, unknown> | null): string | null {
  if (!row) return null
  const id = typeof row.id === 'string' ? row.id : null
  if (!id) return null
  const stamp = typeof row.updated_at === 'string' ? row.updated_at : typeof row.created_at === 'string' ? row.created_at : ''
  return `${table}:${id}:${stamp}`
}

function markAndCheckDuplicate(seen: SeenMap, key: string | null): boolean {
  if (!key) return false
  const now = Date.now()
  // Evict stale fingerprints to keep the map bounded.
  for (const [seenKey, seenAt] of seen) {
    if (now - seenAt > DEDUP_TTL_MS) seen.delete(seenKey)
  }
  if (seen.has(key)) return true
  seen.set(key, now)
  return false
}

/**
 * Subscribes to medication/notification changes. Returns the cleanup
 * function. Throws SUPABASE_NOT_CONFIGURED when the cloud is not configured.
 */
export function subscribeMedicationRealtime(options: SubscribeMedicationRealtimeOptions): () => void {
  const supabase = requireSupabase()
  const seen: SeenMap = new Map()

  let channel: RealtimeChannel | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectAttempts = 0
  let closed = false

  const teardownChannel = () => {
    if (channel) {
      void supabase.removeChannel(channel)
      channel = null
    }
  }

  const handlePayload = (
    table: MedicationRealtimeEvent['table'],
    payload: { eventType: string; new: Record<string, unknown>; old: Record<string, unknown> },
  ) => {
    if (closed) return
    const kind = payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE'
    const incoming = kind === 'DELETE' ? payload.old : payload.new
    const row = incoming && Object.keys(incoming).length > 0 ? incoming : null
    if (markAndCheckDuplicate(seen, fingerprint(table, row))) return

    switch (table) {
      case 'medication_occurrences':
        options.onEvent({
          table,
          kind,
          row: row ? medicationOccurrenceFromRow(row as unknown as MedicationOccurrenceRow) : null,
        })
        break
      case 'medication_intake_events':
        options.onEvent({
          table,
          kind,
          row: row ? medicationIntakeEventFromRow(row as unknown as MedicationIntakeEventRow) : null,
        })
        break
      case 'notifications':
        options.onEvent({
          table,
          kind,
          row: row ? appNotificationFromRow(row as unknown as AppNotificationRow) : null,
        })
        break
    }
  }

  const scheduleReconnect = () => {
    if (closed || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      options.onStatusChange?.('closed')
      return
    }
    reconnectAttempts += 1
    const delay = BASE_BACKOFF_MS * 2 ** (reconnectAttempts - 1)
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      if (!closed) connect()
    }, delay)
  }

  const connect = () => {
    teardownChannel()
    const name = `medication:${options.clientId}:${options.recipientId}`
    channel = supabase
      .channel(name)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'medication_occurrences', filter: `client_id=eq.${options.clientId}` },
        (payload) => handlePayload('medication_occurrences', payload),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'medication_intake_events', filter: `client_id=eq.${options.clientId}` },
        (payload) => handlePayload('medication_intake_events', payload),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `recipient_id=eq.${options.recipientId}` },
        (payload) => handlePayload('notifications', payload),
      )
      .subscribe((status) => {
        if (closed) return
        if (status === 'SUBSCRIBED') {
          reconnectAttempts = 0
          options.onStatusChange?.('subscribed')
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          options.onStatusChange?.('reconnecting')
          scheduleReconnect()
        } else if (status === 'CLOSED') {
          options.onStatusChange?.('closed')
        }
      })
  }

  connect()

  return () => {
    closed = true
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    teardownChannel()
    seen.clear()
  }
}
